import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormControlName, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { FormUtils } from '../../../../../shared/helpers/formUtils';
import { ETrueFalse, EYesNo } from '../../../../../shared/models/types.enum';

@Component({
  selector: 'immediate-concerns',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './immediate-concerns.component.html',
  styleUrl: './immediate-concerns.component.css'
})
export class ImmediateConcernsComponent implements OnInit, OnChanges {

  @Input() rmsPlanChecklistData: any;
  @Output() onSaveSection1 = new EventEmitter<any>();

  yesNo = EYesNo;
  // yesNo = { Yes: 1, No: 0 };
  immediateConcernForm: FormGroup = new FormGroup([]);
  checkList = [
    {
      label: 'Medical Care',
      formControlName: 'medical_care',
      type: 'checkbox',
      dependencies: [
        { label: 'Emergency Medical Response/Ambulance', formControlName: 'emergency_medical_response', type: 'checkbox' },
        { label: 'School Nurse', formControlName: 'school_nurse', type: 'checkbox' },
        { label: 'Planning Notes:', formControlName: 'medical_care_planning_notes', type: 'textarea' }
      ]
    },
    {
      label: 'Law Enforcement Involvement',
      formControlName: 'law_enforcement_involvement',
      type: 'checkbox',
      dependencies: [
        { label: 'No Action Taken', formControlName: 'no_action_taken', type: 'checkbox' },
        { label: 'Ticketed/Charged', formControlName: 'ticketed_charged', type: 'checkbox' },
        { label: 'Arrested/Detained', formControlName: 'arrested_detained', type: 'checkbox' },
        { label: 'Planning Notes:', formControlName: 'law_enforcement_planning_notes', type: 'textarea' }
      ]
    },
    {      
      label: 'Home visit to check for weapons/dangerous materials',
      formControlName: 'home_visit',
      type: 'checkbox',
      dependencies: [
        { label: 'Please explain:', formControlName: 'home_visit_notes', type: 'textarea' }
      ]
    },
    {      
      label: 'Intended victim warned and/or parents/guardians notified',
      formControlName: 'intended_victim',
      type: 'checkbox',
      dependencies: [
        { label: 'Record parent/guardian names and phone numbers and notes taken: *', formControlName: 'intended_victim_notes', type: 'textarea' }
      ]
    },
    {      
      label: 'Suicide Risk Assessment/Screening completed',
      formControlName: 'suicide_risk',
      type: 'checkbox',
      dependencies: [
        { label: 'Planning Notes:', formControlName: 'suicide_risk_notes', type: 'textarea' }
      ]
    },
    {
      label: 'Mental Health Evaluation',
      formControlName: 'mental_health_evaluation',
      type: 'checkbox',
      dependencies: [
        { label: 'Parent Taking Student to Hospital/ER', formControlName: 'parent_student_hospital', type: 'checkbox' },
        { label: 'Ambulance', formControlName: 'ambulance', type: 'checkbox' },
        { label: 'Other', formControlName: 'other', type: 'checkbox' },
        { label: 'Planning Notes:', formControlName: 'mental_health_evaluation_notes', type: 'textarea' }
      ]
    },
    {      
      label: 'Child Abuse Report to Department of Human Services',
      formControlName: 'child_abuse_report',
      type: 'checkbox',
      dependencies: [
        { label: 'Planning Notes:', formControlName: 'child_abuse_report_notes', type: 'textarea' }
      ]
    },
    {      
      label: 'Additional Measures to Ensure Safety',
      formControlName: 'additional_measures',
      type: 'checkbox',
      dependencies: [
        { label: 'Planning Notes:', formControlName: 'additional_measures_notes', type: 'textarea' }
      ]
    },
  ]

  ngOnInit() {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rmsPlanChecklistData'] && changes['rmsPlanChecklistData'].currentValue) {
      this.initializeForm();
    }
  }

  private initializeForm() {
    let group: any = {};
    
    this.checkList.forEach(item => {
      // checkbox behaves as a boolean, so we need to convert it to a number, because the string '0' is considered a "truthy" value because it is a non-empty string, so it's always true
      const defaultValue = this.rmsPlanChecklistData && this.rmsPlanChecklistData[item.formControlName] !== undefined
                           ? this.rmsPlanChecklistData[item.formControlName]
                           : Number(this.yesNo.No);  
      group[item.formControlName] = new FormControl(defaultValue);

      if (item.dependencies) {  
        item.dependencies.forEach(dep => {
          const dependenciesDefaultValue = this.rmsPlanChecklistData && this.rmsPlanChecklistData[dep.formControlName] !== undefined
                             ? this.rmsPlanChecklistData[dep.formControlName]
                             : Number(this.yesNo.No);
          group[dep.formControlName] = dep.type === 'textarea' 
            ? new FormControl(this.rmsPlanChecklistData?.[dep.formControlName] || '') 
            : new FormControl(dependenciesDefaultValue);
        });
      }
    });
    this.immediateConcernForm = new FormGroup(group, { validators: this.atLeastOneParentCheckedValidator() });
    this.immediateConcernForm.addControl('require_roar', new FormControl(String(this.rmsPlanChecklistData?.require_roar !== undefined ? this.rmsPlanChecklistData.require_roar : this.yesNo.No), Validators.required));
  }

  hasCheckboxDependencies(dependencies: any[]): boolean {
    return dependencies.some(dep => dep.type === 'checkbox');
  }

  private atLeastOneParentCheckedValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const formGroup = control as FormGroup;
      const isAtLeastOneChecked = this.checkList.some(item => formGroup.get(item.formControlName)?.value === Number(this.yesNo.Yes));
      return isAtLeastOneChecked ? null : { noParentChecked: true };
    };
  }

  // This function is called when a parent checkbox is checked/unchecked
  onParentCheckboxChange(event: any, controlName: string) {
    const checked = event.target.checked;
    
    const currentItem = this.checkList.find(item => item.formControlName === controlName);
    const dependencies = currentItem!.dependencies;

    if (checked) { // If parent checkbox is checked, enable all dependencies
      this.immediateConcernForm.get(controlName)?.setValue(Number(this.yesNo.Yes));
      dependencies.forEach(dep => {
        if (dep.type === 'textarea'){
          this.immediateConcernForm.get(dep.formControlName)?.setValidators([Validators.required]);
        }
      });
    } else { // If parent checkbox is unchecked, disable all dependencies
      this.immediateConcernForm.get(controlName)?.setValue(Number(this.yesNo.No));
      dependencies.forEach(dep => {
        if (dep.type === 'textarea'){
          this.immediateConcernForm.get(dep.formControlName)?.setValue('');
          this.immediateConcernForm.get(dep.formControlName)?.clearValidators();
        } else if (dep.type === 'checkbox') {
          this.immediateConcernForm.get(dep.formControlName)?.setValue(Number(this.yesNo.No));
        }
      });
    }

    dependencies.forEach(dep => {
      this.immediateConcernForm.get(dep.formControlName)?.updateValueAndValidity();
    });
    this.immediateConcernForm.updateValueAndValidity();

    // // Now, check if at least one parent checkbox is checked
    // const parentCheckboxes = this.checkList.map(item => this.immediateConcernForm.get(item.formControlName));
    // const isAtLeastOneParentChecked = parentCheckboxes.some(control => control?.value === Number(this.yesNo.Yes));

    // // If no parent checkbox is checked, set form as invalid
    // if (!isAtLeastOneParentChecked) {
    //   this.immediateConcernForm.setErrors({ noParentChecked: true });
    // } else {
    //   // remove the error
    //   this.immediateConcernForm.setErrors(null);
    // }
  }

  // This function is called when a checkbox dependency is checked/unchecked
  onDenpendencyCheckboxChange(event: any, parentControlName: string, denpendencyControlName: string) {
    const dependencies = this.checkList.find(item => item.formControlName === parentControlName)!.dependencies;

    if (event.target.checked) {
      this.immediateConcernForm.get(denpendencyControlName)?.setValue(Number(this.yesNo.Yes));
    } else {
      this.immediateConcernForm.get(denpendencyControlName)?.setValue(Number(this.yesNo.No));
    }
      const isAtLeastOneDependencyChecked = dependencies
      .filter(dep => dep.type === 'checkbox') // Only check the checkbox dependencies
      .some(dep => this.immediateConcernForm.get(dep.formControlName)?.value === this.yesNo.Yes);

    // If parent checkbox is checked but no checkbox dependencies are selected, invalidate the parent checkbox
    const parentControl = this.immediateConcernForm.get(parentControlName);
    if (parentControl?.value === this.yesNo.Yes && !isAtLeastOneDependencyChecked) {
      parentControl.setErrors({ noDependenciesChecked: true });
    } else {
      // Remove error if at least one checkbox dependency is selected
      parentControl?.setErrors(null);
    }

    // Update validity after changes
    parentControl?.updateValueAndValidity();
  }

  saveImmediateConcern(): void {
    if (this.immediateConcernForm.valid) {
      this.onSaveSection1.emit(this.immediateConcernForm.value);
    }
  }
}
